import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  Key,
} from 'react';
import AutoCompleteInput, {
  Props as AutoComplete,
} from 'core/components/AutoComplete';
import clsx from 'clsx';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  useSearchPatientHistoryOptions,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import {
  GetApiSettingsGePatientHistoryOptionsParams,
  SettingsDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';
import {
  LookupOptionProps,
} from 'core/components/Lookup/Generic';
import Spinner from 'pages/Dashboard/components/Spinner';

type OptionProps = Omit<LookupOptionProps, 'option'> & {
  option: string;
};

function Option({
  props,
  option,
}: OptionProps) {
  return (
    <li
      {...props}
      className={clsx(
        props?.className,
        'flex flex-row items-center w-full gap-3',
      )}
    >
      <div className="flex flex-col items-start justify-start w-full text-left gap-2">
        <div className="flex flex-row items-center justify-start w-full gap-1 ">
          <span className="font-semibold">
            {option ?? ''}
          </span>
        </div>
      </div>
    </li>
  );
}

type Props = Omit<AutoComplete<string>, 'getLabel' | 'options'> & {
  id: keyof SettingsDTO;
  keyName?: 'source' | 'surgeryName' | 'conditionName' | 'diseaseName' | 'reaction';
};

export default function PatientHistoryAutoComplete({
  id,
  placeholder,
  keyName,
  onChange,
  value,
  ...props
}: Props) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageChanged, setPageChanged] = useState<boolean>(false);
  const {
    data: _data,
    isFetching,
    refetch,
  } = useSearchPatientHistoryOptions({
    type: id,
    search: searchValue,
    page: 0,
    pageSize: (page + 1) * 10,
  } as GetApiSettingsGePatientHistoryOptionsParams);
  const { data = [], pageProperties } = _data ?? {};

  useEffect(() => {
    if (page > 0) {
      refetch();
    }
  }, [page]);

  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (open) {
      setSearchValue(newInputValue);
    }
  }, 700), [open]);

  const onCreateNewOption = useCallback(() => {
    onChange?.(searchValue);
    setOpen(false);
  }, [searchValue]);

  const options = useMemo(
    // Empty string removed internally for initial.
    () => ['Hidden Option', ...(data ?? [])],
    [data],
  );

  const optionSet = new Set(data?.map((option) => option?.toLowerCase()));

  const showAddNew = useMemo(
    () => !isEmptyString(searchValue) && !optionSet.has(searchValue.toLowerCase()),
    [searchValue, optionSet],
  );

  const isLoading = useMemo(() => (
    isFetching && pageChanged
  ), [isFetching, pageChanged]);

  const fetchNextPage = useCallback(() => {
    setPageChanged(true);
    setPage(page + 1);
  }, [page, setPage]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event?.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (showAddNew) {
        onCreateNewOption();
        return;
      }
      if (!isEmptyString(searchValue)) {
        onChange?.(searchValue);
        setOpen(false);
      }
    }
  }, [showAddNew, onCreateNewOption, searchValue]);

  return (
    <AutoCompleteInput<string>
      {...props}
      id={id}
      value={value ?? null}
      onChange={onChange}
      open={open}
      onKeyDown={handleKeyDown}
      onOpen={() => { setOpen(true); }}
      onClose={() => { setOpen(false); }}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option === value}
      openOnFocus
      getOptionDisabled={(option) => option === 'Hidden Option'}
      className="!w-full"
      options={options}
      getLabel={(value) => value ?? ''}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      slotProps={{
        listbox: {
          component: ListboxDropdown,
          pageProperties: isNil(pageProperties) ? null : pageProperties,
          fetchNextPage,
          isLoading,
          isLoadingMore: isLoading,
          isSearching: isFetching,
          count: options?.length,
          id,
          searchValue,
          onAddNew: showAddNew ? onCreateNewOption : undefined,
          label: 'Add',
          shouldRemoveFirstOption: true,
        },
      } as Object}
      dropdownClassName="!overflow-hidden p-2"
      renderOption={(props: Record<string, any>, option: string) => {
        const { key, ...optionProps } = props as typeof props & { key?: Key };

        return (
          <Option
            key={key}
            props={optionProps}
            option={option}
          />
        );
      }}
      popupIcon={isFetching ? <Spinner /> : undefined}
      clearIcon={null}
    />
  );
}
